"use client";

import Image from "next/image";
import Link from "next/link";
import {
  Eye,
  Home,
  School,
  Newspaper,
  DoorOpen,
  MapPinHouse,
  ClipboardList,
  FileUser,
  GraduationCap,
  User,
  SquareUser,
  FilePlus,
  ChartLine,
  Images,
} from "lucide-react";
import MobileNav from "./mobile/mobile-nav";
import { useSessionNav } from "./admin/use-is-admin";
import { SmartDropdown } from "./dropdown/smart-dropdown";
import { usePathname } from "next/navigation";
import { isNavHrefActive, NAV_ICON_CLASS, navLabelClass } from "./nav-active";
import { usePagesVisibility } from "@/hooks/use-pages-visibility";
import { isHrefVisibleForUser } from "@/types/site-settings";

type NavMenuItem = {
  href: string;
  label: string;
  icon: React.ReactNode;
};

function filterNavItems(
  items: NavMenuItem[],
  pages: ReturnType<typeof usePagesVisibility>["pages"],
) {
  return items.filter((item) => isHrefVisibleForUser(item.href, pages));
}

export default function Navbar() {
  const { isAdmin, showGallery, canCreateGallery } = useSessionNav();
  const pathname = usePathname();
  const { pages } = usePagesVisibility();
  const galleryActive = isNavHrefActive(pathname, "/area-riservata/gallery");
  const accountActive = pathname === "/area-riservata";

  const scuolaItems = filterNavItems(
    [
      { href: "/lascuola", label: "Panoramica", icon: <School /> },
      { href: "/news", label: "News", icon: <Newspaper /> },
      { href: "/openday", label: "Open Day", icon: <DoorOpen /> },
    ],
    pages,
  );

  const documentazioneItems = filterNavItems(
    [
      { href: "/documentazione/modulistica", label: "Modulistica", icon: <FileUser /> },
      { href: "/documentazione/circolari", label: "Circolari", icon: <GraduationCap /> },
    ],
    pages,
  );

  const showNews = isHrefVisibleForUser("/news", pages);
  const showOpenday = isHrefVisibleForUser("/openday", pages);

  const galleryCreateItem = {
    href: "/admin/gallery/create-activity",
    label: "Crea Attività",
    icon: <FilePlus />,
  };

  const adminItems = [
    { href: "/area-riservata", label: "Area Riservata", icon: <SquareUser /> },
    { href: "/admin/site-settings", label: "Visibilità pagine", icon: <Eye /> },
    { href: "/admin/circolare/create-circolare", label: "Crea Circolare", icon: <FilePlus /> },
    { href: "/admin/news/create-news", label: "Crea News", icon: <FilePlus /> },
    ...(canCreateGallery ? [galleryCreateItem] : []),
    { href: "/admin/newsletter/create-newsletter", label: "Invia Newsletter", icon: <FilePlus /> },
    { href: "/admin/users-dashboard", label: "Dashboard Utenti", icon: <ChartLine /> },
  ];

  const staffAccountItems = [
    { href: "/area-riservata", label: "Area Riservata", icon: <SquareUser /> },
    galleryCreateItem,
  ];

  return (
    <header className="sticky top-0 left-0 right-0 z-50 h-[110px] bg-(--bg-secondary) w-full flex flex-row items-center justify-between p-6 md:py-6 max-[565px]:px-0">
      <Link
        href="/"
        className="hidden lg:flex items-center mb-4 md:mb-0 cursor-pointer"
        aria-label="Vai alla home"
      >
        <Image
          src="/loghi/logo.png"
          alt="Logo scuola"
          width={150}
          height={80}
        />
      </Link>

      {/* DESKTOP ≥xl */}
      <nav aria-label="desktop-nav" className="hidden xl:flex flex-nowrap justify-center items-center gap-5 xl:gap-6 text-xl">
        <NavItem
          icon={<Home className="h-6 w-6" />}
          label="Home"
          href="/"
          active={isNavHrefActive(pathname, "/")}
        />
        <NavItem
          icon={<School className="h-6 w-6" />}
          label="La Scuola"
          href="/lascuola"
          active={isNavHrefActive(pathname, "/lascuola")}
        />

        <SmartDropdown
          label="Documentazione"
          icon={<ClipboardList className="h-6 w-6" />}
          items={documentazioneItems}
          underlineActive={false}
        />

        {showNews && (
          <NavItem
            icon={<Newspaper className="h-6 w-6" />}
            label="News"
            href="/news"
            active={isNavHrefActive(pathname, "/news")}
          />
        )}
        {showOpenday && (
          <NavItem
            icon={<DoorOpen className="h-6 w-6" />}
            label="Open Day"
            href="/openday"
            active={isNavHrefActive(pathname, "/openday")}
          />
        )}
        <NavItem
          icon={<MapPinHouse className="h-6 w-6" />}
          label="Contatti"
          href="/contatti"
          active={isNavHrefActive(pathname, "/contatti")}
        />

        {showGallery && (
          <NavItem
            icon={<Images className="h-6 w-6" />}
            label="Gallery"
            href="/area-riservata/gallery"
            active={galleryActive}
          />
        )}

        {isAdmin ? (
          <SmartDropdown
            label="Account"
            icon={<User className="h-6 w-6" />}
            items={adminItems}
            labelClass="mt-2 text-base md:text-lg lg:text-xl"
            itemTextClass="text-base md:text-lg lg:text-xl"
            underlineActive={false}
          />
        ) : canCreateGallery ? (
          <SmartDropdown
            label="Account"
            icon={<User className="h-6 w-6" />}
            items={staffAccountItems}
            labelClass="mt-2 text-base md:text-lg lg:text-xl"
            itemTextClass="text-base md:text-lg lg:text-xl"
            underlineActive={false}
          />
        ) : (
          <NavItem
            icon={<User className="h-6 w-6" />}
            label="Account"
            href="/area-riservata"
            active={accountActive}
          />
        )}
      </nav>

      {/* TABLET lg–xl */}
      <nav aria-label="tablet-nav" className="hidden lg:flex xl:hidden flex-nowrap justify-center items-center gap-4 text-lg">
        <NavItem
          icon={<Home className="h-6 w-6" />}
          label="Home"
          href="/"
          active={isNavHrefActive(pathname, "/")}
          underlineActive
        />

        <SmartDropdown
          label="La Scuola"
          icon={<School className="h-6 w-6" />}
          items={scuolaItems}
          labelClass="mt-2 text-base md:text-lg"   
          itemTextClass="text-base md:text-lg"
          underlineActive
        />

        <SmartDropdown
          label="Documentazione"
          icon={<ClipboardList className="h-6 w-6" />}
          items={documentazioneItems}
          labelClass="mt-2 text-base md:text-lg"
          itemTextClass="text-base md:text-lg"
          underlineActive
        />

        <NavItem
          icon={<MapPinHouse className="h-6 w-6" />}
          label="Contatti"
          href="/contatti"
          active={isNavHrefActive(pathname, "/contatti")}
          underlineActive
        />

        {showGallery && (
          <NavItem
            icon={<Images className="h-6 w-6" />}
            label="Gallery"
            href="/area-riservata/gallery"
            active={galleryActive}
            underlineActive
          />
        )}

        {isAdmin ? (
          <SmartDropdown
            label="Account"
            icon={<User className="h-6 w-6" />}
            items={adminItems}
            labelClass="mt-1 text-base md:text-lg"
            itemTextClass="text-base md:text-lg"
            underlineActive
          />
        ) : canCreateGallery ? (
          <SmartDropdown
            label="Account"
            icon={<User className="h-6 w-6" />}
            items={staffAccountItems}
            labelClass="mt-1 text-base md:text-lg"
            itemTextClass="text-base md:text-lg"
            underlineActive
          />
        ) : (
          <NavItem
            icon={<User className="h-6 w-6" />}
            label="Account"
            href="/area-riservata"
            active={accountActive}
            underlineActive
          />
        )}
      </nav>

      {/* Mobile */}
      <MobileNav
        isAdmin={isAdmin}
        showGallery={showGallery}
        canCreateGallery={canCreateGallery}
      />
    </header>
  );
}

function NavItem({
  icon,
  label,
  href,
  active = false,
  underlineActive = false,
}: {
  icon: React.ReactNode;
  label: string;
  href: string;
  active?: boolean;
  underlineActive?: boolean;
}) {
  return (
    <Link
      href={href}
      className="group flex flex-col items-center justify-center font-semibold transition-colors cursor-pointer"
      aria-current={active ? "page" : undefined}
    >
      <span className={NAV_ICON_CLASS}>{icon}</span>
      <span
        className={`mt-2 text-base md:text-lg lg:text-xl ${navLabelClass(active, underlineActive)}`}
      >
        {label}
      </span>
    </Link>
  );
}